import { PageShell } from '@/components/app/page-shell';
import { Skeleton } from '@/components/ui/skeleton';
import { useListAppointments } from '@/features/appointments/api/list-appointments/list-appointments.hook';
import { useGetSubscription } from '@/features/billing';
import { BillingIssueNotice } from '@/features/billing/components/billing-issue-notice';
import {
  useActionRecommendation,
  useActiveRecommendations,
  useWalkthroughDispatcher,
} from '@/features/claire';
import { useListConversations } from '@/features/conversations/api/list-conversations/list-conversations.hook';
import { getGraphicQueryOptions } from '@/features/graphics/api/get-graphic/get-graphic.hook';
import { useGetConnectionHealth } from '@/features/integrations/api';
import { ConnectionHealthUpdate } from '@/features/integrations/components';
import { WHATSAPP_PAYMENT_METHOD_HIGHLIGHT_FLAG } from '@/features/integrations/feature-flags';
import { PendingInvitationsBanner } from '@/features/organization';
import { AdsSetupNextStepCard } from '@/features/setup-progress';
import { useSession } from '@/lib/session';
import { useFlagStatus } from '@/lib/use-flag-status';
import { useResolvedRoutes } from '@/lib/use-routes';
import { cn } from '@/lib/utils';
import type {
  AppointmentWithRelations,
  AssistantPrimaryAction,
  AssistantRecommendationKind,
  ConversationListItem,
} from '@borradh-workspace/api-client/types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Clock,
  Flame,
  Lightbulb,
  MessagesSquare,
  Phone,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  UserCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HomePrompt } from './home-prompt';

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function formatTimeRange(start: string | Date, end: string | Date): string {
  const fmt = (v: string | Date) => {
    const d = typeof v === 'string' ? new Date(v) : v;
    return d.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  };
  return `${fmt(start)}-${fmt(end)}`;
}

function leadDisplayName(appt: AppointmentWithRelations): string {
  // `Serialize<>` widens the optional `lead` relation to include scalar
  // fallbacks; runtime is always `undefined` or the relation object, so guard
  // before reading the relation fields.
  const lead = typeof appt.lead === 'object' && appt.lead ? appt.lead : null;
  const first = lead?.firstName ?? '';
  const last = lead?.lastName ?? '';
  const full = `${first} ${last}`.trim();
  return full || appt.title;
}

function kindIcon(kind: AssistantRecommendationKind): {
  Icon: LucideIcon;
  color: string;
} {
  switch (kind) {
    case 'content_no_post_14_days':
    case 'content_unused_assets':
    case 'content_learning_phase_prompt':
      return { Icon: Sparkles, color: '#9333ea' };
    case 'lead_first_of_session':
    case 'lead_unreplied_2h':
      return { Icon: UserCheck, color: '#2563eb' };
    case 'callback_due':
      return { Icon: Phone, color: '#2563eb' };
    case 'lead_flagged_problem':
      return { Icon: TriangleAlert, color: '#dc2626' };
    case 'booking_confirmed':
    case 'pre_appointment_prep':
      return { Icon: CalendarClock, color: '#16a34a' };
    case 'learning_phase_reassurance':
    case 'campaign_learning_phase_exit':
      return { Icon: Clock, color: '#2563eb' };
    case 'creative_refresh_needed':
    case 'creative_burnout':
      return { Icon: Flame, color: '#ea580c' };
    case 'no_show_surge':
    case 'cpl_spike':
      return { Icon: TriangleAlert, color: '#ca8a04' };
    case 'offer_expiring_soon':
      return { Icon: Clock, color: '#ca8a04' };
    case 'lead_volume_drop':
      return { Icon: TrendingDown, color: '#dc2626' };
    case 'prompt_create_first_ad':
      return { Icon: Target, color: '#16a34a' };
    case 'prompt_create_first_offer':
    case 'prompt_record_first_video':
    case 'prompt_create_first_graphic':
    case 'prompt_create_first_post':
      return { Icon: TrendingUp, color: '#16a34a' };
    default:
      return { Icon: Lightbulb, color: '#6b7280' };
  }
}

const waitingSince = (c: ConversationListItem): Date =>
  new Date(c.lastMessageAt ?? c.createdAt);

function conversationDisplayName(c: ConversationListItem): string {
  return c.externalUserName?.trim() || 'Customer';
}

function TodaysUpdateCard() {
  const todayStart = useMemo(() => startOfToday(), []);
  const todayEnd = useMemo(() => endOfToday(), []);

  const { appointments, isLoading: apptsLoading } = useListAppointments({
    startDateFrom: todayStart,
    startDateTo: todayEnd,
    status: 'booked',
    limit: 50,
  });

  const { conversations, isLoading: convosLoading } = useListConversations({
    status: 'active',
    limit: 50,
  });

  // Broken connections get a card each, above this one.
  const { problems: connectionProblems, isLoading: healthLoading } =
    useGetConnectionHealth();
  const { enabled: paymentHighlightEnabled } = useFlagStatus(
    WHATSAPP_PAYMENT_METHOD_HIGHLIGHT_FLAG
  );
  const problems = connectionProblems.filter(
    (problem) =>
      paymentHighlightEnabled ||
      problem.provider !== 'meta_whatsapp' ||
      problem.reasonCode !== 'meta.whatsapp_payment_blocked'
  );

  const waitingConversations = useMemo(
    () => conversations.filter((c) => c.isUnread),
    [conversations]
  );

  const isLoading = apptsLoading || convosLoading || healthLoading;

  const sortedAppointments = useMemo(
    () =>
      [...appointments].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      ),
    [appointments]
  );

  const apptCount = sortedAppointments.length;
  const unreadCount = waitingConversations.length;
  const hasAnything = apptCount + unreadCount > 0;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">Today's Update</p>
      <ConnectionHealthUpdate problems={problems} />
      {/* Suppressed when it would render "You're all caught up" underneath a
          stack of broken-connection cards — accurate about appointments, but
          a strange thing to read directly below three red ones. */}
      {(hasAnything || problems.length === 0) && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-xs">
          {isLoading ? (
            <div className="flex flex-col gap-5">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
            </div>
          ) : !hasAnything ? (
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  You're all caught up
                </p>
                <p className="text-sm italic text-muted-foreground">
                  No appointments or messages waiting today.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {apptCount > 0 && (
                <div className="flex items-start gap-3">
                  <CalendarDays
                    className="mt-0.5 size-5 shrink-0 text-foreground"
                    strokeWidth={2}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      {apptCount}{' '}
                      {apptCount === 1 ? 'Appointment' : 'Appointments'} Today
                    </p>
                    <ul className="mt-1 flex flex-col gap-1">
                      {sortedAppointments.slice(0, 5).map((appt) => (
                        <li
                          key={appt.id}
                          className="flex items-baseline justify-between gap-3 text-sm italic text-muted-foreground"
                        >
                          <span className="min-w-0 truncate">
                            {leadDisplayName(appt)}
                          </span>
                          <span className="shrink-0 tabular-nums">
                            {formatTimeRange(appt.startDate, appt.endDate)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {unreadCount > 0 && (
                <div className="flex items-start gap-3">
                  <MessagesSquare
                    className="mt-0.5 size-5 shrink-0 text-foreground"
                    strokeWidth={2}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      {unreadCount} Unread{' '}
                      {unreadCount === 1 ? 'Message' : 'Messages'}
                    </p>
                    <ul className="mt-1 flex flex-col gap-1">
                      {waitingConversations.slice(0, 5).map((c) => (
                        <li
                          key={c.id}
                          className="truncate text-sm italic text-muted-foreground"
                        >
                          {conversationDisplayName(c)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** How many waiting conversations get their own row before "+N others". */
const FOLLOW_UP_ROWS = 3;
/** Names fetched for the "+N others" description. */
const FOLLOW_UP_NAMES = 5;

/**
 * People whose latest message is waiting on a reply from us — the same set as
 * the Inbox "Needs Reply" tab. Shows the three most recent, then one row that
 * folds the rest together and names them.
 */
function FollowUpCard() {
  const routes = useResolvedRoutes();
  const { conversations, total, isLoading } = useListConversations({
    tab: 'needs_reply',
    limit: FOLLOW_UP_ROWS + FOLLOW_UP_NAMES,
  });

  if (!isLoading && total === 0) return null;

  const shown = conversations.slice(0, FOLLOW_UP_ROWS);
  const others = conversations.slice(FOLLOW_UP_ROWS);
  const othersCount = Math.max(0, total - FOLLOW_UP_ROWS);
  const otherNames = others.map(conversationDisplayName);
  const nameList =
    otherNames.join(', ') +
    (othersCount > otherNames.length ? ' and more' : '');

  const rowClass =
    'group flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div className="flex flex-col gap-3" data-testid="follow-up-card">
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-muted-foreground">
          Needs a follow-up{total > 0 ? ` (${total})` : ''}
        </p>
        <Link
          to={routes.conversations}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          View all
        </Link>
      </div>
      <div className="rounded-2xl border border-border bg-card p-2 shadow-xs">
        {isLoading ? (
          <div className="flex flex-col gap-3 p-3">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-5 w-56" />
          </div>
        ) : (
          <ul className="flex flex-col">
            {shown.map((c) => (
              <li key={c.id}>
                <Link
                  to={routes.conversations}
                  search={{ id: c.id }}
                  className={rowClass}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'size-2 shrink-0 rounded-full',
                      c.isUnread && 'bg-primary'
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {conversationDisplayName(c)}
                    </p>
                    {c.lastMessageContent ? (
                      <p className="truncate text-sm text-muted-foreground">
                        {c.lastMessageContent}
                      </p>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {formatDistanceToNowStrict(waitingSince(c))}
                  </span>
                  <ChevronRight
                    aria-hidden="true"
                    className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
              </li>
            ))}
            {othersCount > 0 ? (
              <li>
                <Link to={routes.conversations} className={rowClass}>
                  <span
                    aria-hidden
                    className={cn(
                      'size-2 shrink-0 rounded-full',
                      others.some((c) => c.isUnread) && 'bg-primary'
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">
                      +{othersCount} {othersCount === 1 ? 'other' : 'others'}
                    </p>
                    {nameList ? (
                      <p className="truncate text-sm text-muted-foreground">
                        {nameList}
                      </p>
                    ) : null}
                  </div>
                  <ChevronRight
                    aria-hidden="true"
                    className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  );
}

// Kinds whose trigger retires the row itself once the underlying problem is
// fixed. Opening one must not mark it actioned: the lead-waiting nudge should
// stay up until someone actually replies, and its trigger's sweep expires it
// on the first run after the reply lands. A pre-built ad stays up until it is
// launched (or withdrawn): opening it to look is not a decision.
const SELF_RESOLVING_KINDS: ReadonlySet<AssistantRecommendationKind> = new Set([
  'lead_unreplied_2h',
  'ad_optimizer_suggestion',
]);

/** The finished ad's image on a "Launch this?" card, so it is seen before the click. */
function SuggestionThumbnail({ graphicId }: { graphicId: string }) {
  const { data: graphic } = useQuery(getGraphicQueryOptions(graphicId));
  const url = graphic?.outputs?.find((o) => o.status !== 'failed')?.url;
  if (!url) return null;
  return (
    <img
      src={url}
      alt="Preview of the new ad"
      className="size-16 shrink-0 rounded-lg border border-border object-cover"
    />
  );
}

function TodaysRecommendationsCard() {
  const { billingIssue } = useGetSubscription();
  const { recommendations, isLoading, isError } = useActiveRecommendations();
  const { actionRecommendation } = useActionRecommendation();
  const dispatcher = useWalkthroughDispatcher();

  if (
    !billingIssue &&
    !isLoading &&
    (isError || recommendations.length === 0)
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">Today's Recommendations</p>
      <div className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xs">
        {billingIssue ? (
          <BillingIssueNotice issue={billingIssue} compact />
        ) : null}
        {isLoading ? (
          <div className="flex flex-col gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="size-5 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                </div>
              </div>
            ))}
          </div>
        ) : recommendations.length > 0 ? (
          <div className="flex flex-col gap-4">
            {recommendations.slice(0, 3).map((rec) => {
              const { Icon, color } = kindIcon(rec.kind);
              const action = rec.primaryAction as AssistantPrimaryAction;
              const content = (
                <>
                  <Icon
                    className="mt-0.5 size-5 shrink-0"
                    style={{ color }}
                    strokeWidth={2}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      {rec.title}
                    </p>
                    <p className="text-sm italic text-muted-foreground">
                      {rec.body}
                    </p>
                  </div>
                  {rec.kind === 'ad_optimizer_suggestion' &&
                  typeof rec.metadata?.graphicId === 'string' ? (
                    <SuggestionThumbnail graphicId={rec.metadata.graphicId} />
                  ) : null}
                </>
              );

              // Only a `navigate` action with somewhere to go makes the row a
              // link. No tour handlers are registered, so a `tour` row would
              // be a dead click that still marked the nudge done.
              if (action.type !== 'navigate' || !action.target) {
                return (
                  <div key={rec.id} className="flex items-start gap-3">
                    {content}
                  </div>
                );
              }

              // Same semantics as the Claire toast's Accept button: mark the
              // recommendation actioned, then follow its action — except for
              // self-resolving kinds, which stay until their trigger clears
              // them. Navigation does not wait on the write, so a slow or
              // failed mark never strands the owner on home.
              return (
                <button
                  key={rec.id}
                  type="button"
                  aria-label={`${rec.title}. ${action.label}`}
                  onClick={() => {
                    if (!SELF_RESOLVING_KINDS.has(rec.kind)) {
                      actionRecommendation(rec.id);
                    }
                    dispatcher.dispatch(action);
                  }}
                  className="group -m-2 flex items-start gap-3 rounded-xl p-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {content}
                  <ChevronRight
                    aria-hidden="true"
                    className="mt-0.5 size-4 shrink-0 self-center text-muted-foreground transition-transform group-hover:translate-x-0.5"
                  />
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Whether any home content is still hidden behind the pinned prompt. The page
 * scrolls on whichever ancestor owns the overflow, so listen in the capture
 * phase and compare the content's end with the prompt's top.
 */
function useContentBelowPrompt() {
  const endRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const update = () => {
      const end = endRef.current?.getBoundingClientRect().top;
      const prompt = promptRef.current?.getBoundingClientRect().top;
      setVisible(end !== undefined && prompt !== undefined && end > prompt);
    };
    update();
    const observer = new ResizeObserver(update);
    if (endRef.current?.parentElement)
      observer.observe(endRef.current.parentElement);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, []);
  // Scroll the end of the cards up to just above the prompt. The browser
  // picks the scrolling ancestor itself; the scroll margin keeps the end
  // clear of the prompt (and the phone tab bar under it).
  const reveal = () => {
    const end = endRef.current;
    const prompt = promptRef.current;
    if (!end || !prompt) return;
    end.style.scrollMarginBottom = `${window.innerHeight - prompt.getBoundingClientRect().top}px`;
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    end.scrollIntoView({
      block: 'end',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };
  return { endRef, promptRef, visible, reveal };
}

/**
 * "Claire" home — the AI-first home screen. The Ask AI prompt stays pinned
 * while the cards scroll (on screens at least 600px tall), with a fade and a
 * "More below" pill while cards are hidden behind it. On mobile, Ask AI is
 * also available from the floating bottom tab bar on this route.
 */
export function HomeNew() {
  const { data: session, isLoading: isSessionLoading } = useSession();
  const firstName = session?.user?.name?.split(' ')[0] ?? 'there';
  const moreBelow = useContentBelowPrompt();

  if (isSessionLoading) {
    return (
      <>
        <title>Claire | Borrah</title>
        <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]" fillHeight>
          <div className="flex flex-1 flex-col gap-6">
            <div className="mt-6 flex flex-col gap-2">
              <Skeleton className="h-7 w-64" />
              <Skeleton className="h-4 w-44" />
            </div>
            <Skeleton className="h-[160px] w-full rounded-2xl" />
            <Skeleton className="h-[160px] w-full rounded-2xl" />
          </div>
        </PageShell>
      </>
    );
  }

  return (
    <>
      <title>Claire | Borrah</title>
      <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]" fillHeight>
        <div className="flex flex-1 flex-col gap-6">
          <PendingInvitationsBanner />

          <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="mt-6 flex flex-col gap-1">
              <h1 className="text-2xl font-semibold text-foreground">
                Welcome back, {firstName}
              </h1>
              <p className="text-sm text-muted-foreground">
                Here's your update for today
              </p>
            </div>

            <AdsSetupNextStepCard />
            <TodaysUpdateCard />
            <FollowUpCard />
            <TodaysRecommendationsCard />
            <div ref={moreBelow.endRef} aria-hidden />
          </div>

          {/* Claire prompt — pinned to the bottom of the viewport via
              `mt-auto` (PageShell `fillHeight` stretches the column). When the
              cards outgrow the screen the page scrolls, so `sticky` keeps the
              prompt in view; the sidebar-coloured backing hides the cards
              scrolling underneath. `-mb-8 pb-8` reaches into PageShell's
              `pb-8`, keeping the same bottom gap without moving anything.
              Pinned only on screens at least 600px tall: on a short one (a
              phone held sideways) the prompt and tab bar would cover every
              card, so it stays at the end of the page as before. */}
          {/* Home is a bottom-tab route, so below `md` (the app's 768px mobile
              breakpoint) the fixed tab bar covers the page bottom; stick just
              above its 72px row instead (MOBILE_TAB_BAR_ROW_HEIGHT_PX). */}
          <div
            ref={moreBelow.promptRef}
            className="relative bottom-0 z-10 -mb-8 mt-auto bg-sidebar pt-6 pb-8 [@media(min-height:600px)]:sticky [@media(min-height:600px)]:max-md:bottom-[calc(72px+env(safe-area-inset-bottom,0px))]"
          >
            {/* While cards are hidden behind the prompt: fade them into it and
                offer a "More below" pill, so the page reads as scrollable. */}
            <div
              aria-hidden={!moreBelow.visible}
              className={cn(
                'absolute inset-x-0 bottom-full flex h-16 items-end justify-center bg-gradient-to-t from-sidebar to-transparent pb-2 transition-opacity duration-200',
                moreBelow.visible
                  ? 'opacity-100'
                  : 'pointer-events-none opacity-0'
              )}
            >
              <button
                type="button"
                tabIndex={moreBelow.visible ? 0 : -1}
                onClick={moreBelow.reveal}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground shadow-xs hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                More below
                <ChevronDown aria-hidden className="size-3.5" />
              </button>
            </div>
            <HomePrompt />
          </div>
        </div>
      </PageShell>
    </>
  );
}
